import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useChat } from '../context/ChatContext'
import { TrashIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import LoadingSpinner from '../components/common/LoadingSpinner'
import FormattedMessage from '../components/FormattedMessage'
import ConfirmDialog from '../components/ConfirmDialog'
import { eventsService } from '../services/eventsService'

const Chat = () => {
  const { conversationId } = useParams()
  const navigate = useNavigate()
  const messagesEndRef = useRef(null)
  
  const { 
    currentConversation, 
    loadConversation, 
    createConversation, 
    sendMessage,
    deleteConversation,
    clearCurrentConversation,
    loading 
  } = useChat()
  
  const [message, setMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [showOptionsMenu, setShowOptionsMenu] = useState(false)
  const optionsMenuRef = useRef(null)

  // Close options menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target)) {
        setShowOptionsMenu(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  useEffect(() => {
    if (conversationId) {
      loadConversation(conversationId)
    } else {
      clearCurrentConversation()
    }
  }, [conversationId])

  useEffect(() => {
    scrollToBottom()
  }, [currentConversation?.messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // Function to detect event creation requests and automatically create events
  const detectAndCreateEvent = async (messageText) => {
    const eventKeywords = [
      'schedule', 'appointment', 'meeting', 'remind me', 'set up', 'book', 
      'plan', 'arrange', 'create event', 'add event', 'calendar'
    ]
    
    const lowerMessage = messageText.toLowerCase()
    const hasEventKeyword = eventKeywords.some(keyword => lowerMessage.includes(keyword))
    
    if (hasEventKeyword) {
      try {
        // Use AI to extract event details from the message
        const response = await eventsService.getEventSuggestions(
          messageText, 
          `Auto-detected event request from chat message: "${messageText}"`
        )
        
        const suggestions = response.data?.suggestions || []
        
        if (suggestions.length > 0) {
          const suggestion = suggestions[0] // Use the first suggestion
          
          // Create the event automatically
          const eventData = {
            title: suggestion.title || messageText.substring(0, 50),
            date: suggestion.suggestedDate || new Date().toISOString().split('T')[0],
            time: '09:00', // Default time
            type: suggestion.type || 'other',
            description: suggestion.description || `Auto-created from chat: ${messageText}`,
            priority: suggestion.priority || 'medium',
            duration: suggestion.duration || '1 hour',
            aiGenerated: true
          }
          
          await eventsService.createEvent(eventData)
          
          // Show success message
          toast.success(`✅ Event created: "${eventData.title}" on ${eventData.date}`, {
            duration: 4000
          })
          
          return true // Event was created
        }
      } catch (error) {
        console.error('Auto event creation failed:', error)
        // Don't show error to user - this is a background feature
      }
    }
    
    return false // No event created
  }

  const handleSendMessage = async (e) => {
    e.preventDefault()
    
    if (!message.trim()) return

    const messageText = message.trim()
    setMessage('')
    setIsSending(true)

    try {
      // Check for auto event creation in background
      detectAndCreateEvent(messageText).catch(err => 
        console.log('Auto event detection failed:', err)
      )
      
      if (currentConversation) {
        // Send message to existing conversation
        await sendMessage(currentConversation._id, messageText)
      } else {
        // Create new conversation
        const newConversation = await createConversation('New Conversation', messageText)
        navigate(`/chat/${newConversation._id}`, { replace: true })
      }
    } catch (error) {
      toast.error('Failed to send message')
      setMessage(messageText) // Restore message on error
    } finally {
      setIsSending(false)
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage(e)
    }
  }

  const handleDeleteConversation = async () => {
    if (currentConversation) {
      try {
        await deleteConversation(currentConversation._id)
        toast.success('Conversation deleted successfully')
        navigate('/chat')
      } catch (error) {
        toast.error('Failed to delete conversation')
      }
    }
  }

  if (loading && conversationId) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full max-w-4xl mx-auto">
      {/* Chat Header */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 mb-4 sm:mb-6">
        <div className="flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
              {currentConversation?.title || 'New Conversation'}
            </h1>
            {currentConversation && (
              <p className="text-xs sm:text-sm text-gray-600">
                {currentConversation.messages?.length || 0} messages
              </p>
            )}
          </div>
          
          {/* Conversation Options */}
          {currentConversation && (
            <div className="relative" ref={optionsMenuRef}>
              <button
                onClick={() => setShowOptionsMenu(!showOptionsMenu)}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              >
                <EllipsisVerticalIcon className="h-5 w-5" />
              </button>
              
              {showOptionsMenu && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                  <button
                    onClick={() => {
                      setDeleteDialogOpen(true)
                      setShowOptionsMenu(false)
                    }}
                    className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Delete Conversation
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 space-y-4 mb-4 sm:mb-6">
        {!currentConversation && (
          <div className="text-center py-8 sm:py-12">
            <div className="text-4xl sm:text-6xl mb-4">🤖</div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
              Hello! I'm your AI assistant
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 px-4">
              Ask me anything or start a conversation. I'm here to help!
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 max-w-2xl mx-auto px-4">
              <button
                onClick={() => setMessage("What can you help me with today?")}
                className="p-3 sm:p-4 text-left border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all duration-200"
              >
                <div className="font-medium text-gray-900 text-sm sm:text-base">Getting Started</div>
                <div className="text-xs sm:text-sm text-gray-600">What can you help me with?</div>
              </button>
              <button
                onClick={() => setMessage("Can you help me brainstorm some ideas?")}
                className="p-3 sm:p-4 text-left border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all duration-200"
              >
                <div className="font-medium text-gray-900 text-sm sm:text-base">Brainstorming</div>
                <div className="text-xs sm:text-sm text-gray-600">Help me generate ideas</div>
              </button>
              <button
                onClick={() => setMessage("Explain quantum computing in simple terms")}
                className="p-3 sm:p-4 text-left border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all duration-200"
              >
                <div className="font-medium text-gray-900 text-sm sm:text-base">Learning</div>
                <div className="text-xs sm:text-sm text-gray-600">Explain complex topics</div>
              </button>
              <button
                onClick={() => setMessage("Help me write a professional email")}
                className="p-3 sm:p-4 text-left border border-gray-200 rounded-lg hover:border-primary-300 hover:bg-primary-50 transition-all duration-200"
              >
                <div className="font-medium text-gray-900 text-sm sm:text-base">Writing</div>
                <div className="text-xs sm:text-sm text-gray-600">Improve my writing</div>
              </button>
            </div>
          </div>
        )}

        {currentConversation?.messages?.map((msg, index) => (
          <div
            key={index}
            className={`chat-message ${msg.role}`}
          >
            <div className="flex items-start space-x-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                msg.role === 'user' ? 'bg-primary-500' : 'bg-gray-500'
              }`}>
                {msg.role === 'user' ? '👤' : '🤖'}
              </div>
              <div className="flex-1">
                <div className="text-sm text-gray-600 mb-1">
                  {msg.role === 'user' ? 'You' : 'AI Assistant'}
                </div>
                <div className="prose prose-sm max-w-none">
                  {msg.role === 'assistant' ? (
                    <FormattedMessage content={msg.content} />
                  ) : (
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                  )}
                </div>
                {msg.timestamp && (
                  <div className="text-xs text-gray-400 mt-2">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {isSending && (
          <div className="chat-message assistant">
            <div className="flex items-start space-x-3">
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white font-medium bg-gray-500">
                🤖
              </div>
              <div className="flex-1">
                <div className="text-sm text-gray-600 mb-1">AI Assistant</div>
                <div className="flex items-center space-x-2">
                  <LoadingSpinner size="small" />
                  <span className="text-gray-600">Thinking...</span>
                </div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="bg-white border-t border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
        <form onSubmit={handleSendMessage} className="flex space-x-2 sm:space-x-4">
          <div className="flex-1">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message here..."
              className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none resize-none text-sm sm:text-base"
              rows="1"
              style={{ minHeight: '40px', maxHeight: '120px' }}
            />
          </div>
          <button
            type="submit"
            disabled={!message.trim() || isSending}
            className="btn-primary px-3 sm:px-6 py-2 sm:py-3 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base whitespace-nowrap"
          >
            {isSending ? <LoadingSpinner size="small" /> : 'Send'}
          </button>
        </form>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConversation}
        title="Delete Conversation"
        message={`Are you sure you want to delete "${currentConversation?.title}"? All messages will be permanently lost and this action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel" 
        type="danger"
      />
    </div>
  )
}

export default Chat
